import { useEffect, useState } from 'react'
import { InlineWidget, PopupButton, useCalendlyEventListener } from 'react-calendly'

import { REACT_APP_CALENDLY_URL, REACT_APP_CALENDLY_URL_ALTERNATIVE } from 'env'
import { UserClient } from 'api/clients/UserClient'

import { useAuth } from 'hooks/UseAuth'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { SUPPORT_EMAIL } from 'utils/constants'
import { reportError } from 'utils/error'
import ProgressBar from '../ProgressBar/ProgressBar'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { GhostButton } from 'components/Buttons/GhostButton'
import { ReactComponent as LeftArrowIcon } from 'assets/icons/arrowLeft24.svg'
import { ReactComponent as WaveIcon } from 'assets/icons/waveCircle32.svg'
import { ReactComponent as CheckboxIcon } from 'assets/icons/checkboxCircle32.svg'
import { ReactComponent as StarIcon } from 'assets/icons/starCircle32.svg'

import cx from 'classnames'
import Shared from '../../Shared.module.scss'
import Styles from './ScheduleDemo.module.scss'
import { useIsBlaze } from 'hooks/useIsBlaze'

interface Props {
  transitionPage: (page: string) => void
  totalSteps: number
}

export const ScheduleDemo = ({ transitionPage, totalSteps }: Props) => {
  const auth = useAuth() // user should be registered and logged in already. If not go back to signup.
  const workspace = useCurrentWorkspace()
  const companySizeEnum = workspace?.company_size_enum
  const calendlyUrl =
    companySizeEnum === 1 || companySizeEnum === 13 ? REACT_APP_CALENDLY_URL_ALTERNATIVE : REACT_APP_CALENDLY_URL
  const [demoBooked, setDemoBooked] = useState(false)
  const [showPopupButton, setShowPopupButton] = useState(false)
  const [desktopCalendarIsLoading, setDesktopCalendarIsLoading] = useState(true)
  const [mobileCalendarIsLoading, setMobileCalendarIsLoading] = useState(false)
  const isBlaze = useIsBlaze()

  if (!auth) transitionPage('START_OVER')
  if (!workspace) transitionPage('CREATE_WORKSPACE')

  const { isTabletPortrait } = useResponsiveDesign()

  useEffect(() => {
    if (!auth || !demoBooked) return

    const demoScheduledAt = new Date()
    UserClient.updateRegistrationData({ demoScheduledAt })
  }, [auth, demoBooked])

  useEffect(() => {
    setShowPopupButton(true)
  }, [])

  const reportCalendarTimeout = () => {
    showErrorMessageToast('We are having trouble loading the calendar right now')
    reportError(new Error('Calendly took 30+ seconds to load'), {
      severity: 'error',
    })
  }

  useEffect(() => {
    if (isTabletPortrait || !desktopCalendarIsLoading) {
      return
    }

    const timer = setTimeout(() => {
      if (desktopCalendarIsLoading) {
        reportCalendarTimeout()
      }
    }, 30000)

    return () => clearTimeout(timer)
  }, [desktopCalendarIsLoading, isTabletPortrait])

  useEffect(() => {
    if (!isTabletPortrait || !mobileCalendarIsLoading) {
      return
    }

    const timer = setTimeout(() => {
      if (mobileCalendarIsLoading) {
        reportCalendarTimeout()
      }
    }, 30000)

    return () => clearTimeout(timer)
  }, [mobileCalendarIsLoading, isTabletPortrait])

  const onDemoBooked = () => {
    setDemoBooked(true)
    setShowPopupButton(false)
    transitionPage('DEMO_SCHEDULED')
  }

  const markCalendarsAsLoaded = () => {
    setDesktopCalendarIsLoading(false)
    setMobileCalendarIsLoading(false)
  }

  useCalendlyEventListener({
    onEventTypeViewed: () => markCalendarsAsLoaded(),
    onEventScheduled: () => onDemoBooked(),
  })

  const renderPopupButton = (rootDivId: string) => {
    const divToInsertButton = document.getElementById(rootDivId)
    if (!divToInsertButton) return

    return (
      <div
        data-testid="scheduleDemo"
        className={cx(Styles.actionButton, Styles.calendlyPopupButton)}
        onClick={() => setMobileCalendarIsLoading(true)}
      >
        <PopupButton
          url={calendlyUrl}
          // react-calendly uses React's Portal feature (https://reactjs.org/docs/portals.html) to render the popup modal. As a result, you'll need to
          // specify the rootElement property to ensure that the modal is inserted into the correct domNode.
          rootElement={divToInsertButton}
          text="Reserve Onboarding"
          prefill={{
            name: `${auth?.first_name} ${auth?.last_name}`,
            email: auth?.email,
          }}
          pageSettings={{
            hideGdprBanner: true,
            hideEventTypeDetails: true,
          }}
          utm={{
            utmSource: 'registration',
            utmTerm: 'mobile',
          }}
        />
      </div>
    )
  }

  return (
    <div
      data-testid="scheduleDemo"
      className={cx(Shared.content, Styles.content, { [Styles.isTabletPortrait]: isTabletPortrait })}
    >
      <div className={cx(Shared.leftPanel, Styles.leftPanel, { [Styles.isTabletPortrait]: isTabletPortrait })}>
        <div className={cx(Shared.header, Styles.header)}>
          {!workspace?.paying && (
            <GhostButton
              className={cx(Shared.backButton, { [Shared.isBlaze]: isBlaze })}
              aria-label="Back"
              onClick={() => transitionPage('GO_BACK')}
            >
              <LeftArrowIcon />
            </GhostButton>
          )}
          <ProgressBar totalSteps={totalSteps} currentStep={4} />
        </div>
        <div
          className={cx(Shared.largerLeftContent, Styles.largerLeftContent, {
            [Styles.isTabletPortrait]: isTabletPortrait,
          })}
        >
          <div className={cx(Styles.container, { [Styles.isTabletPortrait]: isTabletPortrait })}>
            <div className={cx(Shared.title, Styles.title)}>Get started with personalized onboarding</div>
            <div className={cx(Styles.benefitsWrapper, { [Styles.isTabletPortrait]: isTabletPortrait })}>
              <div className={cx(Styles.benefitDetail, { [Styles.isTabletPortrait]: isTabletPortrait })}>
                <div className={cx(Styles.iconWrapper, { [Styles.isTabletPortrait]: isTabletPortrait })}>
                  <WaveIcon />
                </div>
                <div className={Styles.textWrapper}>
                  We ask all customers to <span>complete a quick white glove onboarding session.</span>
                </div>
              </div>
              <div className={cx(Styles.benefitDetail, { [Styles.isTabletPortrait]: isTabletPortrait })}>
                <div className={cx(Styles.iconWrapper, { [Styles.isTabletPortrait]: isTabletPortrait })}>
                  <CheckboxIcon />
                </div>
                <div className={Styles.textWrapper}>
                  We add your team, migrate your docs, and set up your workspace with{' '}
                  <span>no effort on your part.</span>
                </div>
              </div>
              <div className={cx(Styles.benefitDetail, { [Styles.isTabletPortrait]: isTabletPortrait })}>
                <div className={cx(Styles.iconWrapper, { [Styles.isTabletPortrait]: isTabletPortrait })}>
                  <StarIcon />
                </div>
                <div className={Styles.textWrapper}>
                  Learn how powerful features like Reviews, Track Changes and Tasks{' '}
                  <span>help you make faster, clearer decisions.</span>
                </div>
              </div>
            </div>
            <div className={cx(Styles.buttonsWrapper, { [Styles.isTabletPortrait]: isTabletPortrait })}>
              {isTabletPortrait && showPopupButton && renderPopupButton('scheduleDemoStep')}
              <div
                className={cx(Styles.actionButton, Styles.contactUs, { [Styles.isTabletPortrait]: isTabletPortrait })}
              >
                Can't find a time?{' '}
                <span>
                  <a className={Shared.link} href={`mailto:${SUPPORT_EMAIL}`}>
                    Contact Us
                  </a>
                </span>
              </div>
              {!demoBooked && (
                <GhostButton
                  className={cx(Styles.actionButton, Styles.reserveLater, {
                    [Styles.isTabletPortrait]: isTabletPortrait,
                  })}
                  aria-label="Reserve later"
                  onClick={() => transitionPage('WAITING_LIST')}
                >
                  Reserve later
                </GhostButton>
              )}
            </div>
          </div>
        </div>
      </div>
      {!isTabletPortrait && (
        <div className={Styles.rightContent}>
          <div className={Styles.calendlyWrapper}>
            <div className={Styles.onboardingTitle}>Reserve your onboarding slot</div>
            <InlineWidget
              url={calendlyUrl}
              prefill={{
                name: `${auth?.first_name} ${auth?.last_name}`,
                email: auth?.email,
              }}
              // the height of the widget needs to be slightly shorter so we can do the round border
              pageSettings={{
                hideGdprBanner: true,
                hideEventTypeDetails: true,
              }}
              utm={{
                utmSource: 'registration',
                utmTerm: 'desktop',
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
